import {
  AfterViewInit,
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
} from '@angular/core';

/**
 * Marks a horizontal scroll container while its content is wider than the
 * visible area, so CSS can react only when something is actually hidden.
 *
 * Toggles two classes on the host:
 * - `is-overflowing`  — the content is wider than the container
 * - `is-scrolled-end` — scrolled all the way right; nothing is hidden there
 *
 * First user: the placements tables (Moodle + ILIAS). Their action column
 * (⋮, Speichern, Platzieren) sticks to the right edge while the table scrolls
 * — but its opaque backing and edge shadow may only appear when content can
 * slide underneath it; without overflow the column would show as a strip.
 * CSS alone can't tell (scroll-state queries are Chromium-only).
 *
 * Measured on resize of the host and its direct children (ResizeObserver:
 * window, sidebar, rows loading in) and on scroll — outside the Angular zone,
 * no change detection. The classes are set on the element directly, so the
 * host must not bind `[class]` wholesale (static `class="…"` is fine).
 */
@Directive({
  selector: '[appScrollOverflow]',
})
export class ScrollOverflowDirective implements AfterViewInit, OnDestroy {
  private resizeObserver?: ResizeObserver;
  private frame = 0;
  private readonly onScroll = () => this.schedule();

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    const host = this.el.nativeElement;
    this.zone.runOutsideAngular(() => {
      host.addEventListener('scroll', this.onScroll, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.schedule());
        this.resizeObserver.observe(host);
        Array.from(host.children).forEach((child) => this.resizeObserver!.observe(child));
      }
      this.measure();
    });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
  }

  private schedule(): void {
    if (this.frame) {
      return;
    }
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.measure();
    });
  }

  /** 1px tolerance: `scrollWidth` is rounded, a fractional column width
   *  would otherwise flip the state back and forth. */
  private measure(): void {
    const host = this.el.nativeElement;
    const hidden = host.scrollWidth - host.clientWidth;
    const overflowing = hidden > 1;
    host.classList.toggle('is-overflowing', overflowing);
    host.classList.toggle('is-scrolled-end', overflowing && host.scrollLeft >= hidden - 1);
  }
}
